/**
 * SCENE 06: THE LEVER THAT MOVES THE OCEAN (Renaissance Engineering // 1600 AD)
 * Slender brass ring floating in ocean. Pulling ring downward parts ocean waves down the middle.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';

export class Scene06_LeverOcean implements ISceneLifecycle {
  public id = 5;
  public name = 'The Lever That Moves the Ocean';
  public era = 'Renaissance Engineering (1600 AD)';

  private eventBus: EventBus;
  private store: StateStore;
  private ringPullOffset: number = 0; // 0 to 1.0
  private isParted: boolean = false;
  private waveWallProgress: number = 0;
  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubMove = this.eventBus.subscribe(EventType.INPUT_POINTER_MOVE, (payload) => {
      if (payload.dy && payload.dy > 0 && !this.isParted) {
        this.ringPullOffset = Math.min(1.0, this.ringPullOffset + payload.dy * 0.005);
        this.eventBus.emit(EventType.AUDIO_TRIGGER_FRICTION, payload.x, payload.y, 0, 0, payload.dy * 10);
      }
    });

    this.unsubscribeList.push(unsubMove);
  }

  public update(dt: number): void {
    if (this.ringPullOffset >= 0.8 && !this.isParted) {
      this.isParted = true;
      this.store.setFlag(StateBitmask.OCEAN_PARTED, true);
      this.eventBus.emit(
        EventType.ACCESSIBILITY_ANNOUNCE,
        0, 0, 0, 0, 0, 1, 5,
        'Brass ring engaged! Tempestuous ocean parts down the middle with roaring wave wall, revealing dry glowing seabed passage.'
      );
    }

    if (this.isParted && this.waveWallProgress < 1.0) {
      this.waveWallProgress = Math.min(1.0, this.waveWallProgress + dt * 1.5);
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    const cx = width / 2;
    const cy = height / 2;

    if (this.isParted) {
      // Ocean Parted Down Middle - Dry Glowing Seabed Passage
      const passWidth = width * 0.3 * this.waveWallProgress;

      // Left Wave Wall
      ctx.fillStyle = '#0b2e3d';
      ctx.fillRect(0, 0, cx - passWidth / 2, height);

      // Right Wave Wall
      ctx.fillRect(cx + passWidth / 2, 0, cx - passWidth / 2, height);

      // Dry Glowing Seabed
      ctx.fillStyle = '#d6b885';
      ctx.fillRect(cx - passWidth / 2, 0, passWidth, height);

      ctx.font = '600 13px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#1c2d37';
      ctx.fillText('PARTED OCEAN  //  DRY SEABED PASSAGE', cx, cy);
    } else {
      // Dark Churning Ocean Background
      ctx.fillStyle = '#081c24';
      ctx.fillRect(0, 0, width, height);

      // Slender Brass Ring
      const ringY = cy - 40 + this.ringPullOffset * 80;
      ctx.strokeStyle = '#e0a843';
      ctx.lineWidth = 8;
      ctx.shadowColor = 'rgba(224, 168, 67, 0.8)';
      ctx.shadowBlur = 15;

      ctx.beginPath();
      ctx.arc(cx, ringY, 45, 0, Math.PI * 2);
      ctx.stroke();

      ctx.font = '400 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(220, 200, 160, 0.6)';
      ctx.fillText('[ PULL BRASS RING DOWNWARD TO ENGAGE LEVER ]', cx, height - 40);
    }

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}
}
